/**
 * Payroll PCS — allocation-map editor (primary Phase 1 deliverable).
 * Select employee → AllocationSets → edit lines with live 100% validation.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../AppHeader';
import { payrollAPI } from '../../services/api';

const emptyLine = () => ({
  cfr_code: '',
  cost_center: '',
  program: '',
  percentage: '',
  is_sub: false,
  parent_line: null,
});

function sumPercentages(lines) {
  return lines.reduce((acc, line) => {
    const n = parseFloat(line.percentage);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function isBalanced(total, absolute) {
  if (absolute) return true;
  return Math.abs(total - 100) <= 0.001;
}

const PayrollAllocationEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [sets, setSets] = useState([]);
  const [codes, setCodes] = useState({
    companies: [],
    costCenters: [],
    cfr: [],
    programs: [],
    checkTypes: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [empRes, setRes, co, cc, cfr, prog, ct] = await Promise.all([
        payrollAPI.getEmployee(id),
        payrollAPI.listAllocationSets({ employee: id }),
        payrollAPI.listCompanyCodes(),
        payrollAPI.listCostCenterCodes(),
        payrollAPI.listCFRCodes(),
        payrollAPI.listProgramCodes(),
        payrollAPI.listCheckTypeCodes(),
      ]);
      setEmployee(empRes.data);
      setSets(setRes.data.results || setRes.data || []);
      setCodes({
        companies: co.data.results || co.data || [],
        costCenters: cc.data.results || cc.data || [],
        cfr: cfr.data.results || cfr.data || [],
        programs: prog.data.results || prog.data || [],
        checkTypes: ct.data.results || ct.data || [],
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load employee allocations');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const matchingCostCenters = useMemo(() => {
    if (!form?.company) return codes.costCenters;
    const company = codes.companies.find((c) => c.id === Number(form.company));
    if (!company) return codes.costCenters;
    return codes.costCenters.filter(
      (cc) =>
        cc.code === company.cost_center ||
        cc.matching_company === company.code ||
        form.absolute_indicator
    );
  }, [form, codes]);

  const liveTotal = form ? sumPercentages(form.lines) : 0;
  const liveOk = form ? isBalanced(liveTotal, form.absolute_indicator) : false;

  const openCreate = () => {
    setEditingSet(null);
    setForm({
      company: employee?.company || '',
      cost_center: '',
      check_type: '',
      begin_date: '',
      end_date: '',
      absolute_indicator: false,
      lines: [emptyLine()],
    });
    setDialogOpen(true);
  };

  const openEdit = (allocSet) => {
    setEditingSet(allocSet);
    setForm({
      company: allocSet.company,
      cost_center: allocSet.cost_center || '',
      check_type: allocSet.check_type,
      begin_date: allocSet.begin_date,
      end_date: allocSet.end_date,
      absolute_indicator: allocSet.absolute_indicator,
      lines: (allocSet.lines || []).map((line) => ({
        id: line.id,
        cfr_code: line.cfr_code,
        cost_center: line.cost_center,
        program: line.program,
        percentage: String(line.percentage),
        is_sub: line.is_sub,
        parent_line: line.parent_line,
      })),
    });
    setDialogOpen(true);
  };

  const updateLine = (idx, field, value) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...prev, lines };
    });
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  };

  const removeLine = (idx) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.length <= 1 ? prev.lines : prev.lines.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = async () => {
    if (!form) return;
    if (!form.absolute_indicator && !liveOk) {
      setError('Lines must sum to 100% before saving.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        employee: Number(id),
        company: Number(form.company),
        cost_center: form.absolute_indicator && !form.cost_center ? null : Number(form.cost_center),
        check_type: Number(form.check_type),
        begin_date: form.begin_date,
        end_date: form.end_date,
        absolute_indicator: !!form.absolute_indicator,
        lines: form.lines.map((line) => ({
          cfr_code: Number(line.cfr_code),
          cost_center: Number(line.cost_center || form.cost_center),
          program: Number(line.program),
          percentage: line.percentage,
          is_sub: !!line.is_sub,
          parent_line: line.parent_line || null,
        })),
      };
      if (editingSet) {
        await payrollAPI.updateAllocationSet(editingSet.id, payload);
      } else {
        await payrollAPI.createAllocationSet(payload);
      }
      setDialogOpen(false);
      setForm(null);
      await load();
    } catch (err) {
      const data = err.response?.data;
      setError(
        typeof data === 'object'
          ? Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(' ') : JSON.stringify(v)}`)
              .join('; ')
          : 'Save failed'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (setId) => {
    if (!window.confirm('Delete this allocation set and all its lines?')) return;
    try {
      await payrollAPI.deleteAllocationSet(setId);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) {
    return (
      <>
        <AppHeader backTo="/payroll" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <AppHeader backTo="/payroll" />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/payroll')} sx={{ mb: 1 }}>
          All employees
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h5">
              {employee ? `${employee.last_name}, ${employee.first_name}` : 'Employee'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Paycom {employee?.paycom_number}
              {employee?.company_code ? ` · ${employee.company_code}` : ''}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
            Add allocation set
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {sets.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography color="text.secondary">
              No allocation sets yet. Add a date-range / cost-center / check-type map with lines that sum to 100%.
            </Typography>
          </Paper>
        ) : (
          sets.map((allocSet) => {
            const total = parseFloat(allocSet.percentage_total) || 0;
            const balanced = allocSet.is_balanced;
            return (
              <Paper key={allocSet.id} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1">
                      {allocSet.begin_date} → {allocSet.end_date}
                      {' · '}
                      {allocSet.cost_center_code || (allocSet.absolute_indicator ? 'Absolute' : '—')}
                      {' · '}
                      {allocSet.check_type_code}
                      {' · '}
                      {allocSet.company_code}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${total.toFixed(3)}%`}
                      color={balanced ? 'success' : 'error'}
                      sx={{ mr: 1 }}
                    />
                    {allocSet.absolute_indicator && (
                      <Chip size="small" label="Absolute" variant="outlined" />
                    )}
                  </Box>
                  <Box>
                    <IconButton onClick={() => openEdit(allocSet)} aria-label="Edit">
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(allocSet.id)} aria-label="Delete">
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>CFR</TableCell>
                        <TableCell>Cost center</TableCell>
                        <TableCell>Program</TableCell>
                        <TableCell align="right">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(allocSet.lines || []).map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.cfr_code_display}</TableCell>
                          <TableCell>{line.cost_center_display}</TableCell>
                          <TableCell>{line.program_display}</TableCell>
                          <TableCell align="right">{line.percentage}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            );
          })
        )}
      </Container>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingSet ? 'Edit allocation set' : 'New allocation set'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {form && (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  select
                  label="Company"
                  required
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                >
                  {codes.companies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.code} — {c.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Check type"
                  required
                  value={form.check_type}
                  onChange={(e) => setForm({ ...form, check_type: e.target.value })}
                >
                  {codes.checkTypes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.code} — {c.description}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Cost center"
                  required={!form.absolute_indicator}
                  value={form.cost_center}
                  onChange={(e) => setForm({ ...form, cost_center: e.target.value })}
                  helperText={
                    form.absolute_indicator
                      ? 'Optional when absolute indicator is set'
                      : 'Must correspond to company'
                  }
                >
                  {matchingCostCenters.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.code} — {c.description}
                    </MenuItem>
                  ))}
                </TextField>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.absolute_indicator}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          absolute_indicator: e.target.checked,
                          cost_center: e.target.checked ? form.cost_center : form.cost_center,
                        })
                      }
                    />
                  }
                  label="Absolute allocation (cost center optional)"
                />
                <TextField
                  label="Begin date"
                  type="date"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={form.begin_date}
                  onChange={(e) => setForm({ ...form, begin_date: e.target.value })}
                />
                <TextField
                  label="End date"
                  type="date"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2">Allocation lines</Typography>
                <Chip
                  size="small"
                  label={`${liveTotal.toFixed(3)}%`}
                  color={liveOk ? 'success' : 'error'}
                />
                {!form.absolute_indicator && !liveOk && (
                  <Typography variant="caption" color="error">
                    Must equal 100%
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<Add />} onClick={addLine}>
                  Line
                </Button>
              </Box>

              {form.lines.map((line, idx) => (
                <Box
                  key={idx}
                  sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr 1fr auto', gap: 1 }}
                >
                  <TextField
                    select
                    size="small"
                    label="CFR"
                    value={line.cfr_code}
                    onChange={(e) => updateLine(idx, 'cfr_code', e.target.value)}
                  >
                    {codes.cfr.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.code} — {c.description}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Cost center"
                    value={line.cost_center || form.cost_center}
                    onChange={(e) => updateLine(idx, 'cost_center', e.target.value)}
                  >
                    {matchingCostCenters.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.code}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    label="Program"
                    value={line.program}
                    onChange={(e) => updateLine(idx, 'program', e.target.value)}
                  >
                    {codes.programs.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.program} — {c.description}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="%"
                    type="number"
                    inputProps={{ step: '0.001', min: '0', max: '100' }}
                    value={line.percentage}
                    onChange={(e) => updateLine(idx, 'percentage', e.target.value)}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeLine(idx)}
                    disabled={form.lines.length <= 1}
                    aria-label="Remove line"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              saving ||
              !form ||
              !form.company ||
              !form.check_type ||
              !form.begin_date ||
              !form.end_date ||
              (!form.absolute_indicator && !form.cost_center) ||
              (!form.absolute_indicator && !liveOk)
            }
            onClick={handleSave}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PayrollAllocationEditor;
